<template>
  <div class="location-detail-page overview-page" style="margin-top: 8px">
    <EntityOverviewLayout
      v-if="location"
      :panelTabs="overviewPanels"
      defaultPanel="files"
    >
      <template #top-left>
        <LocationDetails :location="location" @updateLocation="updateLocationDetails" />
      </template>

      <template #top-right>
        <DisplayImageGallery entityType="location" :entityId="location.id" />
      </template>

      <template #bottom-left>
        <ExperimentList :experiments="experiments" />
      </template>

      <template #panel-files>
        <ExperimentFiles
          ref="filesComp"
          :files="files"
          :experimentId="location.id"
          :assId="location.id"
          :assType="'location'"
          :isUploading="isUploading"
          :uploadPercent="uploadPercent"
          :uploadLabel="uploadLabel"
          @addFiles="addFiles"
          @removeFile="removeFile"
        />
      </template>

      <template #panel-comments>
        <ExperimentComments
          :comments="comments"
          :users="users"
          :files="files"
          :entityId="location.id"
          @addComment="addComment"
          @removeComment="removeComment"
          @updateComment="handleUpdateComment"
        />
      </template>
    </EntityOverviewLayout>

    <FileUploadSession
      :visible="uploadSession.visible"
      :fileType="uploadSession.fileType"
      :entityId="uploadSession.entityId"
      :files="uploadSession.files"
      :entraId="loggedInUser?.entra_id"
      :folderId="uploadSession.folderId"
      @complete="handleUploadComplete"
      @close="handleUploadClose"
      @error="handleUploadError"
    />
  </div>
</template>

<script>
import LocationDetails from '@/components/LocationDetails.vue';
import ExperimentList from '@/components/ExperimentList.vue';
import ExperimentFiles from '@/components/ExperimentFiles.vue';
import ExperimentComments from '@/components/ExperimentComments.vue';
import FileUploadSession from '@/components/FileUploadSession.vue';
import EntityOverviewLayout from '@/components/EntityOverviewLayout.vue';
import DisplayImageGallery from '@/components/DisplayImageGallery.vue';
import { useEntityDiscussion } from '@/composables/useEntityDiscussion';
import axios from 'axios';
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import { useToast } from 'vue-toastification';
import { mapState } from 'vuex';

export default {
  components: {
    LocationDetails,
    ExperimentList,
    ExperimentFiles,
    ExperimentComments,
    FileUploadSession,
    EntityOverviewLayout,
    DisplayImageGallery,
  },
  data() {
    return {
      location: null,
      experiments: [],
      files: [],
      comments: [],
      users: [],
      newUserName: '',
      isUploading: false,
      uploadPercent: 0,
      uploadLabel: "",
      uploadSession: {
        visible: false,
        fileType: '',
        entityId: null,
        files: [],
        folderId: null,
      },
    };
  },
  setup() {
    const toast = useToast();
    const discussion = useEntityDiscussion();
    return { toast, discussion };
  },
  created() {
    this.fetchLocationData();
    this.fetchExperiments();
    this.fetchComments();
    this.fetchUsers();
  },
  computed: {
    ...mapState(['loggedInUser']),
    overviewPanels() {
      return [
        { id: 'files', label: 'Files' },
        { id: 'comments', label: 'Comments' },
      ];
    },
  },
  methods: {
    async fetchLocationData() {
      const locationId = this.$route.params.id;
      try {
        const response = await axios.get(API_ENDPOINTS.location(locationId));
        this.location = response.data;
        this.fetchFiles(); // Fetch files after setting location
      } catch (error) {
        this.toast.error('Failed to fetch location data');
        console.error('Failed to fetch location data:', error);
      }
    },
    async fetchExperiments() {
      try {
        const locationId = this.$route.params.id;
        const response = await axios.get(API_ENDPOINTS.locationExperiments(locationId), {
          params: {
            entra_id: this.loggedInUser.entra_id,
          },
        });
        this.experiments = response.data;
      } catch (error) {
        this.toast.error('Failed to fetch experiments');
        console.error('Failed to fetch experiments:', error);
      }
    },
    async fetchFiles() {
      if (!this.location) return;
      const locationId = this.location.id;
      try {
        const response = await axios.get(API_ENDPOINTS.files, {
          params: {
            file_type: 'location',
            entity_id: locationId,
            entra_id: this.loggedInUser.entra_id,
          },
        });
        this.files = response.data;
      } catch (error) {
        if (error.response && error.response.status !== 404) {
            this.toast.error('Failed to fetch files');
            console.error('Failed to fetch files:', error);
          }
      }
    },
    async fetchComments() {
      try {
        this.comments = await this.discussion.fetchComments({
          entityType: 'location',
          entityId: this.$route.params.id,
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error('Failed to fetch comments');
        console.error('Failed to fetch comments:', error);
        console.error('Error details:', error.response ? error.response.data : error.message);
      }
    },
    async fetchUsers() {
      try {
        this.users = await this.discussion.fetchUsers({
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error('Failed to fetch users');
        console.error('Failed to fetch users:', error);
      }
    },
    async addFiles({ files, id, folderId }) {
      this.uploadSession = {
        visible: true,
        fileType: 'location',
        entityId: id,
        files: Array.from(files),
        folderId: folderId || null,
      };
    },

    async handleUploadComplete() {
      await this.fetchFiles();
      if (this.$refs.filesComp?.fetchFolders) {
        await this.$refs.filesComp.fetchFolders();
      }
      this.uploadSession.visible = false;
    },

    handleUploadClose() {
      this.uploadSession.visible = false;
      this.fetchFiles();
    },

    handleUploadError(error) {
      console.error('Upload session error:', error);
    },

    async removeFile(fileId) {
      try {
        await apiService.deleteFile(fileId, this.loggedInUser?.id, this.loggedInUser?.entra_id);
        this.files = this.files.filter(file => file.id !== fileId);
        this.toast.success('File removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove file');
        console.error('Failed to remove file:', error);
      }
    },
    async addComment(commentText, author_id, files) {
      try {
        await this.discussion.addComment({
          entityType: 'location',
          entityId: this.$route.params.id,
          text: commentText,
          authorId: author_id,
          files,
          entraId: this.loggedInUser.entra_id,
        });

        if (files && files.length > 0) {
          await this.fetchFiles();
        }
        await this.fetchComments();
        this.toast.success('Comment added successfully!');
      } catch (error) {
        this.toast.error('Failed to add comment');
        console.error('Failed to add comment:', error);
      }
    },
    async removeComment(commentId) {
      try {
        await this.discussion.removeComment({
          commentId,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        await this.fetchFiles();
        this.toast.success('Comment removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove comment');
        console.error('Failed to remove comment:', error);
      }
    },
    async updateLocationDetails(updatedLocation) {
      const locationId = this.$route.params.id;
      try {
        const response = await apiService.updateResource(`${API_ENDPOINTS.locations}/${locationId}`, updatedLocation);
        this.location = response.data;
        this.fetchLocationData();
        this.toast.success('Location updated successfully!');
      } catch (error) {
        this.toast.error('Failed to update location');
        console.error('Failed to update location:', error);
      }
    },
    async handleUpdateComment(commentId, newText, Id) {
      try {
        await this.discussion.updateComment({
          commentId,
          entityType: 'location',
          entityId: Id ?? this.$route.params.id,
          text: newText,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        this.toast.success("Comment updated successfully!");
      } catch (error) {
        this.toast.error('Failed to update comment');
        console.error('Failed to update comment:', error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.overview-page {
  padding-bottom: 12px;
}
</style>
